// Deferred Sentry facade. `@sentry/react` (~91 KB gzip incl. tracing +
// replay) is dynamically imported after first paint by `sentry.ts` so it
// never lands in the render-critical entry chunk. Every eager consumer
// (api.service interceptor, auth store, route effects, ErrorBoundary)
// calls through this facade instead of importing `@sentry/react`
// directly. Calls made before the SDK loads are buffered and flushed on
// `bindSentry`; if there is no DSN, `disableSentry` drops the queue and
// turns every call into a no-op.
//
// NOTE: only a TYPE import of `@sentry/react` appears here — it is erased
// at build time, so this module carries no runtime Sentry dependency and
// no `import.meta` (keeping it safe for Jest, unlike `sentry.ts`).
import type * as SentryNS from '@sentry/react';

type SentryClient = typeof SentryNS;

let client: SentryClient | null = null;
let disabled = false;

// Bound so a burst of pre-init errors can't grow the queue without limit.
const MAX_QUEUE = 50;
const queue: Array<(s: SentryClient) => void> = [];

function run(fn: (s: SentryClient) => void): void {
  if (client) {
    fn(client);
    return;
  }
  if (disabled) return;
  if (queue.length < MAX_QUEUE) queue.push(fn);
}

/** Called by `initSentry` once the SDK has loaded + `init`'d. */
export function bindSentry(s: SentryClient): void {
  client = s;
  for (const fn of queue) fn(s);
  queue.length = 0;
}

/** Called when there is no DSN — drop buffered calls, no-op future ones. */
export function disableSentry(): void {
  disabled = true;
  queue.length = 0;
}

export function captureException(
  error: unknown,
  context?: Parameters<SentryClient['captureException']>[1]
): void {
  run((s) => s.captureException(error, context));
}

export function addBreadcrumb(
  breadcrumb: Parameters<SentryClient['addBreadcrumb']>[0]
): void {
  run((s) => s.addBreadcrumb(breadcrumb));
}

export function setSentryTag(
  key: string,
  value: Parameters<SentryClient['setTag']>[1]
): void {
  run((s) => s.setTag(key, value));
}

export function setSentryUser(user: {
  id: string;
  email?: string;
  username?: string;
  businessId?: string;
  branchOfficeId?: string;
  role?: string;
}): void {
  run((s) => {
    s.setUser({ id: user.id, email: user.email, username: user.username });
    if (user.businessId) s.setTag('businessId', user.businessId);
    if (user.branchOfficeId) s.setTag('branchOfficeId', user.branchOfficeId);
    if (user.role) s.setTag('role', user.role);
  });
}

export function clearSentryUser(): void {
  run((s) => {
    s.setUser(null);
    s.setTag('businessId', null as unknown as string);
    s.setTag('branchOfficeId', null as unknown as string);
    s.setTag('role', null as unknown as string);
  });
}
